import { Autocomplete, createFilterOptions, TextField } from '@mui/material';
import { forwardRef } from 'react';

type Counterparty = {
  id?: string;
  inputValue?: string;
  name: string;
};

type Props = {
  contacts: Array<{
    id?: string;
    inputValue?: string;
    name: string;
  }>;
  error?: {
    message?: string;
  };
  placeholder?: string;
  value?: Counterparty;
  onChange: (value: Counterparty) => void;
  name?: string;
  disabled?: boolean;
};

const CounterpartySelector = forwardRef<HTMLDivElement, Props>(
  (
    { name, disabled, contacts, error, placeholder, value = '', onChange },
    ref,
  ) => {
    const filter = createFilterOptions<Counterparty>();

    return (
      <Autocomplete
        freeSolo
        disableClearable
        clearOnBlur
        handleHomeEndKeys
        selectOnFocus
        disabled={disabled}
        ref={ref}
        options={contacts}
        value={value}
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            error={!!error}
            helperText={error?.message?.toString()}
            placeholder={placeholder}
            size="small"
          />
        )}
        getOptionKey={(option) =>
          typeof option === 'string' ? option : option.id ?? ''
        }
        getOptionLabel={(option) => {
          // Value selected with enter, right from the input
          if (typeof option === 'string') {
            return option;
          }
          // Add "xxx" option created dynamically
          if (option.inputValue) {
            return option.inputValue;
          }
          // Regular option
          return option.name;
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          const { inputValue } = params;
          const trimmedInputValue = inputValue.trim();

          // Suggest the creation of a new value
          const isExisting = options.some(
            (option) => trimmedInputValue === option.name,
          );
          if (trimmedInputValue !== '' && !isExisting) {
            filtered.push({
              id: undefined,
              inputValue: trimmedInputValue,
              name: `Add "${trimmedInputValue}" as a new counterparty`,
            });
          }

          return filtered;
        }}
        renderOption={(props, option) => {
          return <li {...props}>{option.name}</li>;
        }}
        onChange={(_, newValue) => {
          if (typeof newValue === 'string') {
            onChange({
              id: undefined,
              name: newValue,
            });
          } else if (newValue.inputValue) {
            onChange({
              id: undefined,
              name: newValue.inputValue,
            });
          } else {
            onChange(newValue);
          }
        }}
      />
    );
  },
);

export default CounterpartySelector;
